/**
 * The editable half of a contact, a company or a lead (context.md §106). Phase 76.
 *
 * The record screen was a read-only dump of every field: a CRM where you could see a customer's
 * e-mail address and not correct it, and where fixing a typo meant going back to the list. This is
 * what turns a record into a draft and a draft back into the smallest PATCH that says what changed.
 *
 * Saved once, from a button, like the deal screen: somebody correcting a name and a phone number at
 * the same time means one change, and a PATCH per keystroke is a race with itself.
 *
 * **Which fields are editable is declared, not derived.** A screen that offered every column would
 * offer `created_at` and `version`, and one that guessed from types would offer them the day
 * somebody adds a column. The list is per entity and short because the fields a person actually
 * corrects are few; everything else is still shown, read-only, underneath.
 */

export type EditableKind = 'text' | 'email' | 'phone' | 'url' | 'number' | 'date';

export interface EditableField {
  /** The record's own key, which is also the label key and the PATCH key once camel-cased. */
  key: string;
  kind: EditableKind;
  required?: boolean;
}

/**
 * What each entity lets somebody correct here.
 *
 * Deliberately not the owner, the lifecycle stage or the qualification: each of those has a panel
 * of its own on this screen that knows the rules — who may change it, what it means, what it
 * records — and a second way to set them from a text box would be a second set of rules.
 */
export const EDITABLE: Record<string, EditableField[]> = {
  contacts: [
    { key: 'first_name', kind: 'text', required: true },
    { key: 'last_name', kind: 'text' },
    { key: 'email', kind: 'email' },
    { key: 'phone', kind: 'phone' },
    { key: 'mobile', kind: 'phone' },
    { key: 'job_title', kind: 'text' },
    /*
     * The birthday (Phase 121).
     *
     * The column existed from the first migration with nothing able to write to it, so every
     * contact's birthday was empty and the key-dates screen had nothing to show for it.
     */
    { key: 'birth_date', kind: 'date' },
  ],
  companies: [
    { key: 'name', kind: 'text', required: true },
    { key: 'legal_name', kind: 'text' },
    { key: 'document', kind: 'text' },
    { key: 'email', kind: 'email' },
    { key: 'phone', kind: 'phone' },
    { key: 'website', kind: 'url' },
    { key: 'industry', kind: 'text' },
    { key: 'employee_count', kind: 'number' },
  ],
  leads: [
    { key: 'name', kind: 'text', required: true },
    { key: 'email', kind: 'email' },
    { key: 'phone', kind: 'phone' },
    { key: 'company_name', kind: 'text' },
    { key: 'job_title', kind: 'text' },
  ],
};

export type RecordForm = Record<string, string>;

/** A record as a draft: every editable field as text, because that is what an input holds. */
export function formOf(
  fields: readonly EditableField[],
  data: Record<string, unknown>,
): RecordForm {
  const draft: RecordForm = {};

  for (const field of fields) {
    const value = data[field.key];

    draft[field.key] = value === null || value === undefined ? '' : String(value);
  }

  return draft;
}

/** `first_name` → `firstName`, which is what the API's schemas are written in. */
export function camelCase(key: string): string {
  return key.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase());
}

/**
 * The smallest PATCH that says what changed.
 *
 * Only the keys that actually moved, so two people editing different fields of the same record do
 * not overwrite each other — and an empty box becomes `null` rather than `""`, because a contact
 * with an empty-string e-mail is a contact that matches nothing and looks like it has one.
 */
export function changesBetween(
  fields: readonly EditableField[],
  before: RecordForm,
  after: RecordForm,
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  for (const field of fields) {
    const was = (before[field.key] ?? '').trim();
    const now = (after[field.key] ?? '').trim();

    if (was === now) continue;

    if (field.kind === 'number') {
      changes[camelCase(field.key)] = now === '' ? null : Number(now);
      continue;
    }

    changes[camelCase(field.key)] = now === '' ? null : now;
  }

  return changes;
}

/** What cannot be saved, as field keys, so the screen can say so beside the field. */
export function problemsIn(
  fields: readonly EditableField[],
  draft: RecordForm,
): Record<string, 'required' | 'email' | 'url' | 'number'> {
  const problems: Record<string, 'required' | 'email' | 'url' | 'number'> = {};

  for (const field of fields) {
    const value = (draft[field.key] ?? '').trim();

    if (field.required && value === '') {
      problems[field.key] = 'required';
      continue;
    }

    if (value === '') continue;

    /*
     * Checked here as well as by the server, and for a different reason: the server refuses the
     * whole PATCH, and a person who mistyped one character in an address should be told which
     * field before they press save rather than after.
     */
    if (field.kind === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      problems[field.key] = 'email';
    }

    if (field.kind === 'url' && !/^https?:\/\/\S+$/i.test(value)) {
      problems[field.key] = 'url';
    }

    if (field.kind === 'number' && !/^\d+$/.test(value)) {
      problems[field.key] = 'number';
    }
  }

  return problems;
}
